import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import axios from "./axios";

/**
 * Les invitations d'une societe.
 *
 * ── POURQUOI CE FICHIER EXISTE ──────────────────────────────────────────────
 *
 * L'ecran Utilisateurs d'une societe se nourrit de `/companies/{id}/employees`,
 * qui ne renvoie que les lignes `company_user` — c'est-a-dire les personnes
 * DEJA rattachees. Une personne invitee mais qui n'a pas encore termine son
 * accueil n'y figure nulle part.
 *
 * Consequence pratique : on invite un gestionnaire de flotte, l'ecran affiche
 * "Nombre d'utilisateurs : 0" et "Aucune donnee", et rien ne distingue
 * "jamais invite" de "invite, en attente" de "invite, lien expire". Le seul
 * moyen d'apprendre que quelque chose s'est mal passe etait que l'invite
 * appelle.
 *
 * L'API expose cette liste depuis le debut et auto-expire les lignes perimees
 * dans sa reponse : une invitation `pending` au-dela de 72 h revient en
 * `expired` sans aucune ecriture. Il manquait simplement un appelant de ce
 * cote — codekt-web-app-2 en a un depuis un moment, l'Admin non.
 */

export type TInvitationStatus = "pending" | "accepted" | "revoked" | "expired";

export type TCompanyInvitation = {
  id: number;
  invitation_code: string;
  company_id: number;
  company_email: string;
  company_role: "FLEET_MANAGER" | "EMPLOYEE" | "SUPER_ADMIN";
  status: TInvitationStatus;
  expires_at: string | null;
  accepted_at: string | null;
  invited_by: number | null;
  firstname: string | null;
  lastname: string | null;
  department_id: number | null;
  site_id: number | null;
  created_at: string | null;
  updated_at: string | null;
};

export const invitationKeys = {
  companyList: (companyId: number) =>
    ["invitations", "company", companyId] as const,
};

export const useGetCompanyInvitations = (companyId: number, enabled = true) => {
  const { data, ...res } = useQuery({
    queryKey: invitationKeys.companyList(companyId),
    queryFn: async (): Promise<TCompanyInvitation[]> => {
      const { data } = await axios.get(
        `/companies/${companyId}/employees/invitations`
      );
      return data?.invitations ?? [];
    },
    /*
     * Ces lignes changent des que l'invite ouvre son lien. Les servir depuis le
     * cache ferait afficher "en attente" a un exploitant qui vient de voir la
     * personne entrer — et lui ferait douter de l'ecran, ce qui est pire que de
     * ne pas l'avoir.
     */
    staleTime: 0,
    enabled: enabled && Number.isFinite(companyId) && companyId >= 0,
  });

  return { invitations: data ?? [], ...res };
};

/**
 * Relancer une invitation : renouvelle la fenetre de 72 h et renvoie le mail.
 *
 * Le parametre d'URL est le CODE, pas l'identifiant numerique
 * (CompanyController::resendInvitation cherche sur `invitation_code`). Passer
 * l'id renvoie un 404 silencieux.
 */
export const useResendCompanyInvitation = (companyId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invitationCode: string) => {
      const { data } = await axios.post(
        `/companies/${companyId}/employees/invitation/${invitationCode}/resend`
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: invitationKeys.companyList(companyId),
      });
      toast.success(t("invitation.resent", "Invitation renvoyée"));
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ??
          t("invitation.resendError", "Échec de l'envoi")
      );
    },
  });
};

/** Revoquer une invitation en attente pour que son lien cesse de fonctionner. */
export const useRevokeCompanyInvitation = (companyId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invitationCode: string) => {
      const { data } = await axios.post(
        `/companies/${companyId}/employees/invitation/${invitationCode}/revoke`
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: invitationKeys.companyList(companyId),
      });
      toast.success(t("invitation.revoked", "Invitation révoquée"));
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ??
          t("invitation.revokeError", "Échec de la révocation")
      );
    },
  });
};
